import React, { useEffect, useMemo, useState } from 'react';
import { ExcitedIcon } from 'src/components/Quiz/Icons/ExcitedIcon';
import { CuriousIcon } from 'src/components/Quiz/Icons/CuriousIcon';
import { SkepticalIcon } from 'src/components/Quiz/Icons/SkepticalIcon';
import { Typography } from '@guildeducationinc/recess';
import { Footer } from 'src/components/Quiz/Footer';
import { trackEvent } from 'src/services/analytics';
import { QuizQuestion } from 'src/components/Quiz/QuizQuestion';
import { AnswerKeys, QuizAnalytics, ScreenProps } from '@src/containers/QuizContainer/types';
import { useQuiz } from '@src/components/Providers/QuizProvider';
import styles from './Feeling.module.scss';

const question = 'How are you feeling about going back to school?';

export const Feeling: React.FC<ScreenProps> = props => {
	const { answers } = useQuiz();
	const { defaultAnswers, nextScreen, previousScreen, updateAnswers } = props;
	const defaultAnswer = answers?.feeling ? answers.feeling : defaultAnswers?.feeling || undefined;
	const [feelingAnswer, setFeelingAnswer] = useState<string>(defaultAnswer);

	const analyticsData: QuizAnalytics = useMemo(() => {
		return {
			questionKey: question,
			questionType: 'radioButtons',
		};
	}, []);

	useEffect(() => {
		setFeelingAnswer(defaultAnswer);
	}, [defaultAnswer]);

	useEffect(() => {
		updateAnswers({ [AnswerKeys.feeling]: feelingAnswer }, answers);
	}, [answers, feelingAnswer, updateAnswers]);

	useEffect(() => {
		trackEvent('userProgramQuizQuestionAttempted', analyticsData);
	}, [analyticsData]);

	return (
		<>
			<fieldset className={styles.container} data-testid='feeling'>
				<legend className={styles.question} id='feelings-label'>
					<QuizQuestion question={question} />
				</legend>
				<div
					aria-labelledby='feelings-label required'
					className={styles.answerContainer}
					role='radiogroup'
				>
					<button
						aria-checked={feelingAnswer === 'excited'}
						className={styles.answer}
						data-selected={feelingAnswer === 'excited'}
						data-testid='excited'
						onClick={() => setFeelingAnswer('excited')}
						onKeyDown={() => setFeelingAnswer('excited')}
						role='radio'
					>
						<ExcitedIcon size={84} />
						<div className={styles.textContainer}>
							<Typography variant='h2'>Excited</Typography>
							<Typography className={styles.subtext} variant='label1'>
								I&apos;m ready to get started.
							</Typography>
						</div>
					</button>
					<button
						aria-checked={feelingAnswer === 'curious'}
						className={styles.answer}
						data-selected={feelingAnswer === 'curious'}
						data-testid='curious'
						onClick={() => setFeelingAnswer('curious')}
						onKeyDown={() => setFeelingAnswer('curious')}
						role='radio'
					>
						<CuriousIcon size={84} />
						<div className={styles.textContainer}>
							<Typography variant='h2'>Curious</Typography>
							<Typography className={styles.subtext} variant='label1'>
								I&apos;m interested, but I need more information.
							</Typography>
						</div>
					</button>
					<button
						aria-checked={feelingAnswer === 'skeptical'}
						className={styles.answer}
						data-selected={feelingAnswer === 'skeptical'}
						data-testid='skeptical'
						onClick={() => setFeelingAnswer('skeptical')}
						onKeyDown={() => setFeelingAnswer('skeptical')}
						role='radio'
					>
						<SkepticalIcon size={84} />
						<div className={styles.textContainer}>
							<Typography variant='h2'>Skeptical</Typography>
							<Typography className={styles.subtext} variant='label1'>
								I&apos;m not sure, but I want to learn more.
							</Typography>
						</div>
					</button>
				</div>
			</fieldset>
			<Footer
				disableNext={!feelingAnswer}
				onClickNext={() => {
					analyticsData.responseKey = feelingAnswer;
					updateAnswers({ [AnswerKeys.feeling]: feelingAnswer }, answers);
					trackEvent('userProgramQuizQuestionCompleted', analyticsData);
					nextScreen();
				}}
				onClickPrevious={previousScreen}
			/>
		</>
	);
};
